import { useState } from "react";
import { Gift, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Reveal from "@/components/Reveal";

const Loyalty = () => {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.get("/loyalty", { params: { email } });
      setResult(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const progress = result ? Math.min(result.orders, 10) / 10 : 0;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-8">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Programme Privilège</p>
        <h1 className="mt-2 font-serif text-3xl text-[#1A1714] sm:text-4xl">La fidélité récompensée</h1>
        <p className="mt-3 text-sm leading-relaxed text-[#5C5449]">
          Chez ANM Paris, votre fidélité compte : à partir de <strong className="text-[#1A1714]">10 commandes</strong>,
          un parfum vous est <strong className="text-[#1A1714]">offert</strong> par la maison.
          Entrez l'email utilisé lors de vos commandes pour voir votre progression.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <form onSubmit={submit} className="mt-8 flex gap-3 rounded-2xl border border-[#EADFCB] bg-white p-5">
          <input
            data-testid="loyalty-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre email de commande"
            className="flex-1 rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]"
          />
          <button
            data-testid="loyalty-check-button"
            disabled={loading}
            className="flex items-center gap-2 rounded-xl bg-[#C59B27] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#A8821B] active:scale-95 disabled:opacity-50"
          >
            <Search size={15} /> {loading ? "…" : "Vérifier"}
          </button>
        </form>
      </Reveal>

      {result && (
        <Reveal className="mt-8 rounded-3xl border border-[#EADFCB] bg-white p-8" >
          <div data-testid="loyalty-progress-card">
            <div className="flex items-center justify-between">
              <p className="font-serif text-xl font-semibold text-[#1A1714]">Votre progression</p>
              <p className="font-accent text-2xl text-[#C59B27]">{Math.min(result.orders, 10)} / 10</p>
            </div>
            <div className="mt-4 h-3 overflow-hidden rounded-full bg-[#F7EEDB]">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#C59B27] to-[#FF8A00] transition-all duration-1000"
                style={{ width: `${progress * 100}%` }}
              />
            </div>
            {result.reward_reached ? (
              <div className="mt-6 flex items-center gap-4 rounded-2xl bg-[#1A1714] p-5">
                <Gift size={32} className="shrink-0 text-[#C59B27]" />
                <div>
                  <p className="font-serif text-lg font-semibold text-[#F7EEDB]">Félicitations, parfum offert !</p>
                  <p className="text-sm text-[#F7EEDB]/70">
                    Vous avez atteint {result.orders} commandes. Contactez-nous sur WhatsApp pour choisir votre parfum offert.
                  </p>
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-[#5C5449]">
                {result.orders === 0
                  ? "Aucune commande trouvée avec cet email pour le moment. Votre première commande vous rapprochera du cadeau !"
                  : `Plus que ${10 - result.orders} commande${10 - result.orders > 1 ? "s" : ""} avant votre parfum offert.`}
              </p>
            )}
          </div>
        </Reveal>
      )}
    </div>
  );
};

export default Loyalty;
